import { FormComponentProps } from '@ant-design/compatible/lib/form';
import {
  Row,
  Col,
  Input,
  Select,
  InputNumber,
  Switch,
  Button,
  Modal,
} from 'antd';
import React, { useState } from 'react';
import { getClient } from '../../apollo';
import { Form, Icon as LegacyIcon } from '@ant-design/compatible';
import {
  GetARateQuery,
  useUpdateServiceSubtypeMutation,
} from '../../graphql/datamodel.gen';
import { capitalizeFirstChar } from '../../utils/string';

interface ISubtypeFormValues {
  name: string;
  description?: string;
  threshold: number;
}

interface ISubtypeForm extends FormComponentProps {
  subtype: ServiceSubtype;
  onSubmit: (data: ISubtypeFormValues) => any;
}

interface ISubtypeFormState {
  conditionType: 'DISTANCE' | 'TIME';
  threshold: number;
}

type ServiceSubtype =
  GetARateQuery['rate']['serviceTypes'][number]['subtypes'][number];

interface SubtypeModalProps {
  visible: boolean;
  onClose: () => void;
  subtype?: ServiceSubtype;
  rateId: string;
}

class SubtypeForm extends React.Component<ISubtypeForm, ISubtypeFormState> {
  state = {
    conditionType: undefined,
    threshold: undefined,
  };

  constructor(props: Readonly<ISubtypeForm>) {
    super(props);

    this.state = {
      conditionType: props.subtype.fulfillingCondition.conditionType,
      threshold: props.subtype.fulfillingCondition.threshold,
    };
  }

  handleSubmit = (e) => {
    e.preventDefault();

    this.props.form.validateFields((err, values: ISubtypeFormValues) => {
      if (!err) {
        this.props.onSubmit({
          description: values.description,
          name: values.name,
          threshold: values.threshold,
        });
      }
    });
  };

  render() {
    const { getFieldDecorator } = this.props.form;

    const localizedConditionType =
      this.state.conditionType === 'DISTANCE'
        ? 'distancia'
        : this.state.conditionType === 'TIME'
        ? 'duración'
        : this.state.conditionType === 'LOCATION'
        ? 'localización'
        : '';

    const units =
      this.state.conditionType === 'DISTANCE'
        ? 'Km'
        : this.state.conditionType === 'TIME'
        ? 'horas'
        : '';

    const value =
      this.state.conditionType === 'DISTANCE'
        ? this.state.threshold / 1000
        : this.state.conditionType === 'TIME'
        ? this.state.threshold / 3600
        : undefined;

    return (
      <Form
        id={'update-subtype-form'}
        onSubmit={this.handleSubmit}
        layout="vertical"
      >
        <Row>
          <Col span={12}>
            <Form.Item
              style={{ marginRight: '15px' }}
              label={'Nombre del subtipo'}
              required={true}
            >
              {getFieldDecorator('name', {
                initialValue: this.props.subtype.name,
                rules: [
                  {
                    required: true,
                    message: 'Por favor introduce un nombre para el subtipo',
                  },
                ],
              })(<Input />)}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={'Descripción'} style={{ flex: 1 }}>
              {getFieldDecorator('description', {
                initialValue: this.props.subtype.description,
                rules: [
                  {
                    required: false,
                  },
                ],
              })(<Input.TextArea />)}
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            <Form.Item
              style={{ marginRight: '15px' }}
              label={'Tipo de condición'}
              required={true}
            >
              {getFieldDecorator('conditionType', {
                initialValue: this.state.conditionType,
                rules: [
                  {
                    required: false,
                  },
                ],
              })(
                <Select
                  disabled={true}
                  onChange={(value) => {
                    this.setState({
                      conditionType: value as 'DISTANCE' | 'TIME',
                    });
                  }}
                >
                  <Select.Option key={'DISTANCE'} value={'DISTANCE'}>
                    Distancia (Kilómetros)
                  </Select.Option>
                  <Select.Option key={'TIME'} value={'TIME'}>
                    Duración (horas)
                  </Select.Option>
                  <Select.Option key={'LOCATION'} value={'LOCATION'}>
                    Localización
                  </Select.Option>
                </Select>,
              )}
            </Form.Item>
          </Col>
          <Col span={12}>
            {this.state.conditionType === 'DISTANCE' && (
              <Form.Item
                label={capitalizeFirstChar(localizedConditionType)}
                required={true}
              >
                {getFieldDecorator('threshold', {
                  initialValue: value,
                  rules: [
                    {
                      required: false,
                    },
                  ],
                  // @ts-ignore
                })(
                  <InputNumber
                    addonBefore="Hasta"
                    min={1}
                    addonAfter={units}
                  />,
                )}{' '}
              </Form.Item>
            )}
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            {this.props.subtype.hours > 0 && (
              <Form.Item label={capitalizeFirstChar('horas')} required={true}>
                {getFieldDecorator('hours', {
                  initialValue: this.props.subtype.hours,
                  rules: [
                    {
                      required: false,
                    },
                  ],
                  // @ts-ignore
                })(
                  <InputNumber
                    addonBefore="Minimo"
                    disabled
                    min={1}
                    addonAfter={'horas'}
                  />,
                )}{' '}
              </Form.Item>
            )}
          </Col>
          <Col span={12}>
            {this.props.subtype.dinner && (
              <Form.Item label={capitalizeFirstChar('cena')} required={true}>
                {getFieldDecorator('dinner', {
                  initialValue: this.props.subtype.dinner,
                  rules: [
                    {
                      required: false,
                    },
                  ],
                  // @ts-ignore
                })(
                  <Switch checked={this.props.subtype.dinner} disabled />,
                )}{' '}
              </Form.Item>
            )}
          </Col>
        </Row>
      </Form>
    );
  }
}

const MySubtypeForm = Form.create<ISubtypeForm>()(SubtypeForm);

export const SubtypeModal: React.FC<SubtypeModalProps> = (
  props: SubtypeModalProps,
) => {
  const [loading, setLoading] = useState(false);

  const [updateServiceSubtypeMut] = useUpdateServiceSubtypeMutation({
    client: getClient(),
  });

  const footerButtons = [
    <Button danger style={{ float: 'left' }} onClick={props.onClose}>
      Cerrar sin guardar
    </Button>,
    <Button
      loading={loading}
      form="update-subtype-form"
      key="submit"
      htmlType="submit"
      type={'primary'}
    >
      Guardar cambios
    </Button>,
  ];

  const updateRate = async (data: ISubtypeFormValues) => {
    // Update it
    try {
      setLoading(true);
      await updateServiceSubtypeMut({
        variables: {
          id: props.subtype.id,
          description: data.description,
          distanceInMeters: data.threshold * 1000,
          name: data.name,
          rateId: props.rateId,
        },
      });
      props.onClose();
    } catch (e) {
      console.error('Error updating Service Subtype', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={`Subtipo de servicio: ${props.subtype?.name}`}
      width={800}
      afterClose={props.onClose}
      okText={'Cerrar'}
      onCancel={props.onClose}
      onOk={props.onClose}
      footer={footerButtons}
      visible={props.visible}
      centered={true}
      destroyOnClose={false}
      forceRender={true}
    >
      {props.subtype && (
        <MySubtypeForm subtype={props.subtype} onSubmit={updateRate} />
      )}
    </Modal>
  );
};
